import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import ServiceCard from "./ServiceCard";

import hairImage from "@/assets/friseur-haarstyling-nuernberg.webp";
import facialImage from "@/assets/gesichtsbehandlung-facial-nuernberg.webp";
import facialVideo from "@/assets/gesichtsbehandlung-video-nuernberg.webm";
import haarentfernungImage from "@/assets/diodenlaser-haarentfernung-beine-nuernberg.webp";
import lashesImage from "@/assets/lash-brow-lift-behandlung-nuernberg.webp";
import nailsImage from "@/assets/gelnaegel-nagelstudio-nuernberg.webp";
import nailsVideo from "@/assets/nageldesign-video-nuernberg.webm";
const services = [
  {
    id: "haare",
    title: "Haare",
    description:
      "Professionelle, hochwertige Haarbehandlungen, Haarschnitte, Pflege und Stylings mit exklusiven Paul Mitchell Produkten. Für gesundes, glänzendes Haar und deinen perfekten Look – individuell abgestimmt auf deinen Stil.",
    cta: "Mehr erfahren",
    image: hairImage,
    link: "/nuernberg/haare",
    tags: ["Balayage", "Coloration", "Styling"],
  },
  {
    id: "facials",
    title: "Gesichtsbehandlung",
    description:
      "Gesichtsbehandlungen für strahlende Haut. Erlebe moderne Facials mit Glow Solution, Aquafacial, Mikroneedling und exklusiven iS Clinical Behandlungen – für sichtbare Ergebnisse und entspannte Wellnessmomente.",
    cta: "Mehr erfahren",
    image: facialImage,
    video: facialVideo,
    link: "/nuernberg/facials",
    tags: ["Glow Solution", "Aquafacial", "Mikroneedling"],
  },
  {
    id: "haarentfernung",
    title: "Dauerhafte Haarentfernung",
    description:
      "Glatte Haut – dauerhaft. Mit dem ALIX Secret Twin Gerät bieten wir modernste Diodenlaser-Technologie für sanfte und effektive dauerhafte Haarentfernung. Für alle Hauttypen geeignet.",
    cta: "Mehr erfahren",
    image: haarentfernungImage,
    link: "/nuernberg/haarentfernung",
    tags: ["Diodenlaser", "Alle Hauttypen", "Schmerzarm"],
  },
  {
    id: "wimpern",
    title: "Wimpern Lifting",
    description:
      "Natürlicher Schwung für Wimpern und Augenbrauen – ganz ohne Extensions. Lash Lifting und Brow Lift sorgen für einen wachen, offenen Blick, der bis zu 8 Wochen hält.",
    cta: "Mehr erfahren",
    image: lashesImage,
    link: "/nuernberg/facials",
    tags: ["Lash Lift", "Brow Lift", "Kombi"],
  },
  {
    id: "naegel",
    title: "Nägel",
    description:
      "Kreative Nageldesigns, die Ihre Persönlichkeit widerspiegeln und Ihre Hände zum Strahlen bringen. Modern, gepflegt und stilvoll – perfekt abgestimmt auf Ihren Look.",
    cta: "Mehr erfahren",
    image: nailsImage,
    video: nailsVideo,
    link: "/nuernberg/naegel",
    tags: ["Maniküre", "Gelnägel", "Shellack"],
  },
];

const Services = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const basePath = "/nuernberg";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-20 bg-transparent relative z-10">
      <div className="container px-6">
        {/* Section Header */}
        <div className="text-center mb-20 animate-on-scroll opacity-0 translate-y-8">
          <div className="inline-block px-8 py-6 rounded-2xl bg-foreground/5 backdrop-blur-md border border-foreground/10">
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className="w-12 h-px bg-gradient-to-r from-transparent to-highlight" />
              <span className="text-sm tracking-wide uppercase text-label font-medium">
                Unsere Behandlungen
              </span>
              <span className="w-12 h-px bg-gradient-to-l from-transparent to-highlight" />
            </div>
            <h2 className="text-heading md:text-display font-serif font-light text-foreground">
              <span className="text-highlight font-medium">Premium</span> Beauty Services
            </h2>
          </div>
        </div>

        {/* Services Grid */}
        <div className="space-y-24 md:space-y-32">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className="animate-on-scroll opacity-0 translate-y-8"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <ServiceCard
                {...service}
                link={service.link.startsWith("/nuernberg") ? service.link.replace("/nuernberg", basePath) : service.link}
                reverse={index % 2 !== 0}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
